import { OidcSsrAdapter } from '@msw/oidc-ssr-adapter';

import {
  clearAppSessionStorage,
  isLegacyAuthProvider,
  isMosAuthProvider,
  setMosAuthProvider,
} from '@commons/auth/session';
import { config, MOS_DEFAULT_SCOPE } from '../../config';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@commons/utils/sessionStorage';
import {
  hasMosRefreshToken,
  isMosAccessTokenExpired,
  isMosAccessTokenExpiredOrWithinRenewWindow,
  shouldResolveMosAccessToken,
  shouldRetryMosUnauthorized,
} from './accessToken';
import {
  isMosOidcBootstrapPath,
  MOS_CALLBACK_PATH,
  MOS_COMPLETE_PATH,
  MOS_FORCE_REAUTH_STORAGE_KEY,
  MOS_ID_TOKEN_HINT_STORAGE_KEY,
  MOS_LOGOUT_PATH,
  MOS_RETURN_URL_STORAGE_KEY,
  MOS_START_LOGIN_AFTER_LOGOUT_KEY,
} from '@commons/auth/constants';

const getMosConfig = () => config.mos || {};

export const isMosLoginConfigured = () => {
  const { issuer, clientId } = getMosConfig();

  return Boolean(issuer && clientId);
};

const getCallbackRedirectUri = () => {
  const { redirectUri } = getMosConfig();

  if (redirectUri) {
    return redirectUri;
  }

  return `${window.location.origin}${MOS_CALLBACK_PATH}`;
};

const getPostLogoutRedirectUri = () => {
  const { postLogoutRedirectUri } = getMosConfig();

  if (postLogoutRedirectUri) {
    return postLogoutRedirectUri;
  }

  return `${window.location.origin}${MOS_LOGOUT_PATH}`;
};

const getDefaultScope = () => {
  const { scope } = getMosConfig();

  return scope || MOS_DEFAULT_SCOPE;
};

/**
 * Singleton adapter. Reused across login / callback / token / logout.
 */
let oidcMosAdapter;
let mosRenewInFlight;

const resetOidcMosAdapter = () => {
  oidcMosAdapter = undefined;
  mosRenewInFlight = undefined;
};

/**
 * @msw/oidc-ssr-adapter 0.3.2 hardcodes automaticSilentRenew: false.
 * MOS issues refresh_token (offline_access) and /connect/userinfo is CORS-safe,
 * so we keep the adapter default loadUserInfo: true and only enable silent renew.
 */
const configureMosUserManager = (adapter) => {
  const userManager = adapter?.oidc;

  if (!userManager) {
    return;
  }

  if (userManager.settings) {
    userManager.settings.automaticSilentRenew = true;
  }

  if (typeof userManager.signinSilent === 'function') {
    const originalSigninSilent = userManager.signinSilent.bind(userManager);

    userManager.signinSilent = async (args) => {
      const user = await userManager.getUser();

      if (!user?.refresh_token) {
        throw new Error('MOS refresh token missing; silent renew is disabled');
      }

      return originalSigninSilent(args);
    };
  }

  if (typeof userManager.startSilentRenew === 'function') {
    userManager.startSilentRenew();
  }
};

/**
 * Returns the shared OidcSsrAdapter. Managed session:
 * session.redirectUri + session.postLoginRedirectUri.
 */
export const getOidcMosAdapter = async () => {
  if (!isMosLoginConfigured()) {
    throw new Error('MOS OIDC is not configured');
  }

  if (oidcMosAdapter) {
    return oidcMosAdapter;
  }

  oidcMosAdapter = (async () => {
    const { issuer, clientId, debug } = getMosConfig();

    const adapter = new OidcSsrAdapter({
      issuer,
      clientId,
      scope: getDefaultScope(),
      debug: Boolean(debug),
      session: {
        type: 'managed',
        redirectUri: getCallbackRedirectUri(),
        postLoginRedirectUri: `${window.location.origin}${MOS_COMPLETE_PATH}`,
      },
    });

    configureMosUserManager(adapter);

    return adapter;
  })();

  return oidcMosAdapter;
};

const normalizeReturnUrl = (returnUrl) => {
  if (!returnUrl || typeof returnUrl !== 'string') {
    return '/home';
  }

  // Prevent open redirects — only allow same-origin relative paths.
  if (!returnUrl.startsWith('/') || returnUrl.startsWith('//')) {
    return '/home';
  }

  return returnUrl;
};

/**
 * Reads the @msw/oidc-ssr-adapter user entry (access_token, refresh_token, expires_at).
 */
const getOidcUserStorageKey = (issuer, clientId) => `oidc.user:${issuer}:${clientId}`;

const readStoredMosOidcUser = () => {
  if (!isMosLoginConfigured()) {
    return null;
  }

  const { issuer, clientId } = getMosConfig();
  const storedOidcUser = window.localStorage.getItem(getOidcUserStorageKey(issuer, clientId));

  if (!storedOidcUser) {
    return null;
  }

  try {
    return JSON.parse(storedOidcUser);
  } catch {
    return null;
  }
};

const readStoredIdToken = () => readStoredMosOidcUser()?.id_token;

/**
 * True when adapter/OIDC storage still has a user entry (including expired).
 * Used after bootstrap 401 when authProvider was never set to mos.
 */
export const hasStoredMosOidcUser = () => {
  if (!isMosLoginConfigured()) {
    return false;
  }

  const { issuer, clientId } = getMosConfig();

  return Boolean(window.localStorage.getItem(getOidcUserStorageKey(issuer, clientId)));
};

/**
 * Drop OIDC user from localStorage immediately (sync). clearSsrSession is async and can race.
 */
const removeStoredMosOidcUser = () => {
  if (!isMosLoginConfigured()) {
    return;
  }

  const { issuer, clientId } = getMosConfig();

  window.localStorage.removeItem(getOidcUserStorageKey(issuer, clientId));
};

const fetchEndSessionEndpoint = async (issuer) => {
  const metadataUrl = `${issuer.replace(/\/$/, '')}/.well-known/openid-configuration`;
  const response = await fetch(metadataUrl);

  if (!response.ok) {
    return null;
  }

  const metadata = await response.json();

  return metadata.end_session_endpoint || null;
};

/**
 * Redirects to MOS end-session (clears IdP SSO cookies). Returns false if redirect could not start.
 */
const redirectToMosEndSession = async ({ idTokenHint } = {}) => {
  const { issuer, clientId } = getMosConfig();

  if (!issuer || !clientId) {
    return false;
  }

  try {
    const endSessionEndpoint = await fetchEndSessionEndpoint(issuer);

    if (!endSessionEndpoint) {
      return false;
    }

    const logoutUrl = new URL(endSessionEndpoint);

    logoutUrl.searchParams.set('client_id', clientId);
    logoutUrl.searchParams.set('post_logout_redirect_uri', getPostLogoutRedirectUri());

    if (idTokenHint) {
      logoutUrl.searchParams.set('id_token_hint', idTokenHint);
    }

    window.location.assign(logoutUrl.toString());

    return true;
  } catch {
    return false;
  }
};

/**
 * Starts MOS Authorization Code + PKCE via managed handleSsrSession.
 * Clears leftover InPulse/LoopBack keys so callback/complete are not treated as legacy.
 * Clears any leftover OIDC user first — otherwise handleSsrSession sees getUser()
 * "user loaded" (e.g. after 401/expiry) and skips the authorize redirect.
 * After a 401 local clear, forces MOS end-session first so SSO does not silently re-auth the same user.
 */
export const startMosLogin = async ({ returnUrl } = {}) => {
  setSessionStorageItem(MOS_RETURN_URL_STORAGE_KEY, normalizeReturnUrl(returnUrl));
  clearAppSessionStorage();

  const forceReauth = getSessionStorageItem(MOS_FORCE_REAUTH_STORAGE_KEY) === '1';

  if (forceReauth) {
    const idTokenHint = getSessionStorageItem(MOS_ID_TOKEN_HINT_STORAGE_KEY);

    removeSessionStorageItem(MOS_ID_TOKEN_HINT_STORAGE_KEY);

    try {
      const adapter = await getOidcMosAdapter();

      await adapter.clearSsrSession();
    } catch {
      // Continue — sync remove below still drops local OIDC state.
    }

    removeStoredMosOidcUser();
    resetOidcMosAdapter();

    setSessionStorageItem(MOS_START_LOGIN_AFTER_LOGOUT_KEY, '1');

    const redirected = await redirectToMosEndSession({ idTokenHint });

    if (redirected) {
      // Keep MOS_FORCE_REAUTH until MosLogout clears it — if the user abandons
      // end-session and clicks Login again, we still must not SSO silently.
      return;
    }

    // End-session unavailable — fall through to authorize (SSO may still apply).
    removeSessionStorageItem(MOS_START_LOGIN_AFTER_LOGOUT_KEY);
    removeSessionStorageItem(MOS_FORCE_REAUTH_STORAGE_KEY);
  }

  const adapter = await getOidcMosAdapter();

  try {
    await adapter.clearSsrSession();
  } catch {
    // Continue — sync remove below still forces a fresh login.
  }

  removeStoredMosOidcUser();

  await adapter.handleSsrSession();
};

/**
 * Completes the OIDC callback. Managed mode redirects to postLoginRedirectUri (/complete).
 */
export const completeMosCallback = async () => {
  const adapter = await getOidcMosAdapter();

  await adapter.handleSsrSession();
};

const renewMosSessionOnce = async () => {
  if (mosRenewInFlight) {
    return mosRenewInFlight;
  }

  mosRenewInFlight = (async () => {
    const adapter = await getOidcMosAdapter();

    await adapter.refreshSsrSession();
  })().finally(() => {
    mosRenewInFlight = undefined;
  });

  return mosRenewInFlight;
};

/**
 * Return a usable MOS access token, renewing via refresh_token when expired or
 * inside the renew window.
 */
const ensureValidMosAccessToken = async () => {
  const user = readStoredMosOidcUser();

  if (!user?.access_token) {
    return undefined;
  }

  if (!isMosAccessTokenExpiredOrWithinRenewWindow(user)) {
    return user.access_token;
  }

  if (!hasMosRefreshToken(user) && isMosAccessTokenExpired(user)) {
    return undefined;
  }

  if (!hasMosRefreshToken(user)) {
    return user.access_token;
  }

  try {
    await renewMosSessionOnce();
  } catch {
    if (isMosAccessTokenExpired(user)) {
      return undefined;
    }

    return user.access_token;
  }

  return readStoredMosOidcUser()?.access_token;
};

export const getMosAccessToken = async () => {
  if (!isMosLoginConfigured()) {
    return undefined;
  }

  const canResolveMosToken = shouldResolveMosAccessToken({
    isLegacySession: isLegacyAuthProvider(),
    isMosBootstrapPath: isMosOidcBootstrapPath(),
  });

  if (!canResolveMosToken) {
    return undefined;
  }

  return ensureValidMosAccessToken();
};

/**
 * Starts UserManager silent renew for an existing MOS session (page reload / idle).
 */
export const startMosTokenRenewal = async () => {
  if (!isMosLoginConfigured() || !isMosAuthProvider()) {
    return;
  }

  try {
    await getOidcMosAdapter();
  } catch {
    // Adapter init failure must not block app boot.
  }
};

/**
 * One renew attempt after a MOS 401. Returns the access token to retry with, or
 * undefined when renew is not possible (caller then clears the local MOS session).
 */
export const tryRenewMosAccessToken = async () => {
  if (!isMosLoginConfigured() || isLegacyAuthProvider()) {
    return undefined;
  }

  const user = readStoredMosOidcUser();

  if (!hasMosRefreshToken(user)) {
    return undefined;
  }

  if (!isMosAccessTokenExpiredOrWithinRenewWindow(user) && user?.access_token) {
    return user.access_token;
  }

  try {
    await renewMosSessionOnce();
  } catch {
    return undefined;
  }

  return readStoredMosOidcUser()?.access_token;
};

export const shouldRetryFailedMosRequest = ({ alreadyRetried, sentAuthorization } = {}) => {
  const user = readStoredMosOidcUser();

  return shouldRetryMosUnauthorized({
    isMosSession: isMosAuthProvider(),
    alreadyRetried,
    user,
    sentAuthorization,
  });
};

export const hasMosAccessToken = async () => {
  const accessToken = await getMosAccessToken();

  return Boolean(accessToken);
};

/**
 * Marks the InPulse session as MOS-authenticated after a successful OIDC login.
 */
export const markMosSession = () => {
  setMosAuthProvider();
};

export const consumeMosReturnUrl = () => {
  const returnUrl = normalizeReturnUrl(getSessionStorageItem(MOS_RETURN_URL_STORAGE_KEY));

  removeSessionStorageItem(MOS_RETURN_URL_STORAGE_KEY);

  return returnUrl;
};

/**
 * Clears local MOS/OIDC + InPulse session without calling Mapal OS end-session.
 * Used on API 401 so a failed profile bootstrap does not strand the user on the MOS logout page.
 * Stashes id_token_hint + force-reauth so the next Login with MOS ends SSO before authorize.
 */
export const clearMosLocalSession = async () => {
  if (isMosLoginConfigured()) {
    const idToken = readStoredIdToken();

    if (idToken) {
      setSessionStorageItem(MOS_ID_TOKEN_HINT_STORAGE_KEY, idToken);
    }

    setSessionStorageItem(MOS_FORCE_REAUTH_STORAGE_KEY, '1');
  }

  // Sync first: 401 handler does not await; login must not see a stale OIDC user.
  removeStoredMosOidcUser();

  if (isMosLoginConfigured()) {
    try {
      const adapter = await getOidcMosAdapter();

      await adapter.clearSsrSession();
    } catch {
      // Continue local cleanup even if OIDC clear fails.
    }
  }

  resetOidcMosAdapter();
  clearAppSessionStorage();
};

/**
 * Clears InPulse keys, clears adapter storage, then redirects to MOS end-session.
 * Uses discovery + optional id_token_hint from adapter storage.
 * MOS cookie/signout invalidates tokens server-side.
 */
export const logoutMos = async () => {
  const { issuer, clientId } = getMosConfig();

  if (!issuer || !clientId) {
    clearAppSessionStorage();
    resetOidcMosAdapter();
    window.location.assign('/login');

    return;
  }

  const idToken = readStoredIdToken();

  clearAppSessionStorage();

  try {
    const adapter = await getOidcMosAdapter();

    await adapter.clearSsrSession();
  } catch {
    // Continue with end-session even if local OIDC clear fails.
  }

  resetOidcMosAdapter();

  const redirected = await redirectToMosEndSession({ idTokenHint: idToken });

  if (!redirected) {
    window.location.assign('/login');
  }
};

export default {
  isMosLoginConfigured,
  getOidcMosAdapter,
  hasStoredMosOidcUser,
  startMosLogin,
  completeMosCallback,
  getMosAccessToken,
  hasMosAccessToken,
  startMosTokenRenewal,
  tryRenewMosAccessToken,
  shouldRetryFailedMosRequest,
  markMosSession,
  consumeMosReturnUrl,
  clearMosLocalSession,
  logoutMos,
};
